import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Truck, TrendingUp, Package, ExternalLink, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { StockItem } from "@/types/stock-item";

interface StockItemSupplierTabProps {
  item: StockItem;
  isEditMode: boolean;
  onItemChange?: (item: StockItem) => void;
  validationErrors?: Record<string, string>;
  touchedFields?: Set<string>;
  onFieldBlur?: (field: keyof StockItem) => void;
}

// Mock supplier list
const SUPPLIERS = [
  "ABC Electronics Ltd",
  "Global Furniture Co",
  "Office Depot Inc",
  "TechSupply Solutions",
  "Industrial Materials Group",
  "Premier Office Supplies",
  "BuildMart Wholesale",
  "Quality Tools & Equipment",
  "SafetyFirst Supplies",
  "PackagePro Distributors",
  "MetalWorks International",
  "Furniture Direct Ltd",
  "ElectroTech Suppliers",
  "Office Essentials Co",
  "Hardware & More Inc"
];

// Format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(amount);
};

// Helper to ensure we have a valid Date object (handles JSON-serialized dates)
const ensureDate = (value: Date | string | null | undefined): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

// Format date
const formatDate = (date: Date | string | null | undefined): string => {
  const validDate = ensureDate(date);
  if (!validDate) return "-";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(validDate);
};

// Calculate days ago
const getDaysAgo = (date: Date | string | null | undefined): number => {
  const validDate = ensureDate(date);
  if (!validDate) return 0;
  const now = new Date();
  const diff = now.getTime() - validDate.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

// Calculate next reorder due date
const getNextReorderDue = (item: StockItem): { date: Date | null; isOverdue: boolean } => {
  if (item.physicalQuantity <= item.reorderPoint) {
    return { date: null, isOverdue: true };
  }
  
  // Estimate based on stock turn rate
  if (item.stockTurnRate > 0) {
    const daysUntilReorder = Math.floor((item.physicalQuantity - item.reorderPoint) / (item.stockTurnRate / 365));
    const nextDate = new Date();
    nextDate.setDate(nextDate.getDate() + daysUntilReorder);
    return { date: nextDate, isOverdue: false };
  }
  
  return { date: null, isOverdue: false };
};

// Calculate estimated delivery date
const getEstimatedDelivery = (leadTimeDays?: number): Date | null => {
  if (!leadTimeDays) return null;
  const deliveryDate = new Date();
  deliveryDate.setDate(deliveryDate.getDate() + leadTimeDays);
  return deliveryDate;
};

export function StockItemSupplierTab({ item, isEditMode, onItemChange }: StockItemSupplierTabProps) {
  const hasSupplier = !!item.supplier;
  const hasPurchaseHistory = !!ensureDate(item.lastPurchaseDate);
  const hasSaleHistory = !!ensureDate(item.lastSaleDate);
  
  const nextReorder = getNextReorderDue(item);
  const estimatedDelivery = getEstimatedDelivery(item.leadTimeDays);

  // Get performance color
  const getPerformanceColor = (rate?: number) => {
    if (!rate) return "text-muted-foreground";
    if (rate >= 95) return "text-green-600 dark:text-green-400";
    if (rate >= 85) return "text-orange-600 dark:text-orange-400";
    return "text-red-600 dark:text-red-400";
  };

  if (!hasSupplier && !isEditMode) {
    return (
      <Card className="p-8">
        <div className="flex flex-col items-center justify-center text-center space-y-3">
          <div className="rounded-full bg-muted p-3">
            <Package className="h-8 w-8 text-muted-foreground" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">No Supplier Assigned</h3>
            <p className="text-sm text-muted-foreground mt-1">
              This item doesn't have a supplier assigned yet.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Primary Supplier Section */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold mb-4">Primary Supplier</h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="supplier">Supplier</Label>
            <Select
              value={item.supplier || "none"}
              onValueChange={(value) => onItemChange?.({ ...item, supplier: value === "none" ? undefined : value })}
              disabled={!isEditMode}
            >
              <SelectTrigger 
                id="supplier"
                className={`${!isEditMode ? 'bg-muted border-0 cursor-default' : ''}`}
              >
                <SelectValue placeholder="Select supplier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {SUPPLIERS.map((supplier) => (
                  <SelectItem key={supplier} value={supplier}>
                    {supplier}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Primary supplier for this item</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="leadTime">Lead Time</Label>
            <div className="relative">
              <Input
                id="leadTime"
                type="number"
                min="0"
                value={item.leadTimeDays || ""}
                onChange={(e) => onItemChange?.({ ...item, leadTimeDays: parseInt(e.target.value) || undefined })}
                placeholder="0"
                readOnly={!isEditMode}
                className={`pr-12 ${!isEditMode ? 'bg-muted border-0 cursor-default' : ''}`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                days
              </span>
            </div>
            <p className="text-xs text-muted-foreground">Average delivery time from supplier</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="supplierContact">Supplier Contact</Label>
            <Input
              id="supplierContact"
              type="email"
              value={item.supplierContact || ""}
              onChange={(e) => onItemChange?.({ ...item, supplierContact: e.target.value })}
              placeholder="supplier@example.com"
              readOnly={!isEditMode}
              className={`${!isEditMode ? 'bg-muted border-0 cursor-default' : ''}`}
            />
            <p className="text-xs text-muted-foreground">Primary contact email</p>
          </div>
        </div>
      </Card>

      {/* Purchase History Card */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Purchase History
        </h3>
        {hasPurchaseHistory ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Last Purchase Date</Label>
              <div className="text-sm font-medium">
                {formatDate(item.lastPurchaseDate!)}
              </div>
              <div className="text-xs text-muted-foreground">
                ({getDaysAgo(item.lastPurchaseDate!)} days ago)
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Last Purchase Quantity</Label>
              <div className="text-sm font-medium font-mono">
                {item.lastPurchaseQuantity?.toLocaleString() || "-"}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Last Purchase Price</Label>
              <div className="text-sm font-medium font-mono">
                {item.lastPurchasePrice ? formatCurrency(item.lastPurchasePrice) : "-"}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground py-4 text-center">
            No purchase history
          </div>
        )}
      </Card>

      {/* Sales History Card */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          Sales History
        </h3>
        {hasSaleHistory ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Last Sale Date</Label>
              <div className="text-sm font-medium">
                {formatDate(item.lastSaleDate!)}
              </div>
              <div className="text-xs text-muted-foreground">
                ({getDaysAgo(item.lastSaleDate!)} days ago)
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Last Sale Quantity</Label>
              <div className="text-sm font-medium font-mono">
                {item.lastSaleQuantity?.toLocaleString() || "-"}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Last Sale Price</Label>
              <div className="text-sm font-medium font-mono">
                {item.lastSalePrice ? formatCurrency(item.lastSalePrice) : "-"}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground py-4 text-center">
            No sales history
          </div>
        )}
      </Card>

      {/* Reorder Information Card */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
          <Truck className="h-4 w-4" />
          Reorder Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Next Reorder Due</Label>
            {nextReorder.isOverdue ? (
              <Badge variant="destructive" className="mt-1">
                <AlertCircle className="h-3 w-3 mr-1" />
                Order now
              </Badge>
            ) : nextReorder.date ? (
              <div className="text-sm font-medium">
                {formatDate(nextReorder.date)}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">-</div>
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Suggested Order Quantity</Label>
            <div className="text-sm font-medium font-mono">
              {item.reorderQuantity?.toLocaleString() || "-"}
            </div>
            <div className="text-xs text-muted-foreground">Based on reorder settings</div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Estimated Delivery</Label>
            {estimatedDelivery ? (
              <div className="text-sm font-medium">
                {formatDate(estimatedDelivery)}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">-</div>
            )}
          </div>
        </div>
      </Card>

      {/* Supplier Performance Indicators */}
      {hasSupplier && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold mb-4">Supplier Performance</h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">On-Time Delivery</Label>
                <span className={`text-sm font-semibold ${getPerformanceColor(item.onTimeDeliveryRate)}`}>
                  {item.onTimeDeliveryRate?.toFixed(1)}%
                </span>
              </div>
              <Progress 
                value={item.onTimeDeliveryRate || 0} 
                className="h-2"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Average Lead Time</Label>
                <div className="text-sm font-medium">
                  {item.averageLeadTime ? `${item.averageLeadTime} days` : "-"}
                </div>
                {item.averageLeadTime && item.leadTimeDays && (
                  <div className="text-xs text-muted-foreground">
                    {item.averageLeadTime > item.leadTimeDays ? (
                      <span className="text-orange-600 dark:text-orange-400">
                        +{item.averageLeadTime - item.leadTimeDays} days vs expected
                      </span>
                    ) : item.averageLeadTime < item.leadTimeDays ? (
                      <span className="text-green-600 dark:text-green-400">
                        {item.leadTimeDays - item.averageLeadTime} days faster
                      </span>
                    ) : (
                      <span>On target</span>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Orders This Year</Label>
                <div className="text-sm font-medium font-mono">
                  {item.totalOrdersYTD?.toLocaleString() || "-"}
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* View Full Supplier Details Link */}
      {hasSupplier && (
        <div className="flex justify-center pt-2">
          <button className="text-sm text-primary hover:underline flex items-center gap-1">
            View full supplier details
            <ExternalLink className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}
